import React from 'react';

import {useSnackbar} from 'notistack';
import {Link, useHistory} from 'react-router-dom';
import {Add, CheckSharp} from '@material-ui/icons';
import {Box, Divider, Grid, Typography} from '@material-ui/core';

import {
    CONSIGNEES_NAME,
    CONSIGNEES_PER_PAGE,
    CONSIGNEE_NAME,
    Constants,
    HistoryRoutes,
    Messages,
    SnackbarVariants,
} from 'commons/constants';
import {ClientService, DocumentServices} from 'services';
import {useAlert, useSpinner} from 'hooks';
import {MainContent} from 'layouts/commons';
import {nextStep, notEmpty, useViewSearch} from 'commons/utils/Functions';
import ConsigneeForm from './consignee-form/ConsigneeForm';
import EfeModalMsg from 'components/efe-modal-msg/EfeModalMsg';
import {EfeAccordionValues, EfeViewsArray, Pagination} from 'commons/models';
import {ConsigneeData, ConsigneeTerms} from 'commons/types/Types';
import EfePagination from 'components/efe-pagination/EfePagination';
import {EfeAccordion, EfeButtonSecondary, EfeSave} from 'components';
import {useMenuContext} from 'context/onboarding-menu/OnboardingMenu';
import {useFlowContext} from 'context/onboarding-flow/OnboardingFlow';
import {ReactComponent as ActionIcon} from 'assets/icons/section/action/edit.svg';
import {useRelatedPeopleContext} from 'context/related-people-flow/RelatedPeopleFlow';
import {LinkStyles} from 'assets/tss/layouts/components';
import {ReactComponent as DownloadIcon} from 'assets/icons/section/action/download.svg';
import {Utils} from 'commons';

export default function Consignee() {
    const history = useHistory();
    const {enqueueSnackbar} = useSnackbar();
    const {flow, moduleStep, updateModuleStep} = useFlowContext();
    const {consigneesConfig, setConsigneesConfig} = useRelatedPeopleContext();
    const {expedientId, menu, fatherKycId, setKycId, kycId} = useMenuContext();
    const {showAlert, defaultError, genericError, showAlertError, closeAlert} = useAlert();

    const [page, setPage] = React.useState<number>(1);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [consignees, setConsignees] = React.useState<Array<ConsigneeData>>([]);
    const [paginationData, setPaginationData] = React.useState<Pagination>();
    const [showModal, setShowModal] = React.useState<boolean>(!consigneesConfig?.numberConsignees);
    const views: Array<EfeViewsArray> = useViewSearch({...flow}, [...menu]);
    const classesLink = LinkStyles();

    function handleAddConsignee() {
        setKycId(-1);
        setShowModal(false);
        history.push(HistoryRoutes.CONSIGNEE_INFO);
    }

    async function saveUserConsigneesConfig(values: ConsigneeTerms) {
        setIsLoading(true);
        try {
            const response = await ClientService.saveUserConsigneesConfig(values);
            if (response && response.data) {
                setConsigneesConfig(values);
                enqueueSnackbar(response.data.message, {
                    variant: SnackbarVariants.SUCCESS,
                });
            } else {
                enqueueSnackbar(Messages.ERROR_SAVING_INFORMATION, {
                    variant: SnackbarVariants.ERROR,
                });
            }
        } catch (error) {
            showAlertError(error, Messages.ERROR_SAVING_INFORMATION);
        } finally {
            setIsLoading(false);
        }
    }

    async function getUserConfig() {
        setIsLoading(true);
        try {
            const response = await ClientService.userHasConsignees(expedientId);
            if (response && response.data && response.data.expedientId) {
                setConsigneesConfig(response.data);
                await getConsigneeList(1);
                setShowModal(!notEmpty(response.data.numberConsignees));
            }
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            setIsLoading(false);
        }
    }

    function handleChange(kycIdSelected: number) {
        setKycId(kycIdSelected);
        history.push(HistoryRoutes.CONSIGNEE_INFO);
    }

    async function getConsigneeList(page: number) {
        setPage(page);
        try {
            setIsLoading(true);
            const response = await ClientService.getConsignees(
                fatherKycId,
                page,
                CONSIGNEES_PER_PAGE
            );
            if (response && response.data && response.data.data && response.data.data.length) {
                setConsignees(response.data.data);
                setPaginationData(response.data.pagination);
            }
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
            console.error(error);
        }
    }

    async function downloadContract(idkyc: number) {
        setIsLoading(true);
        try {
            if (expedientId && idkyc) {
                const response = await DocumentServices.getDocumentByKycId(
                    Constants.DOC_CONTRATO_CT_PDF,
                    expedientId,
                    idkyc,
                    'pdf'
                );
                Utils.functions.downloadDocs(response, `${Constants.DOC_CONTRATO_CT_PDF}.pdf`);
            }
            enqueueSnackbar(Messages.FILE_DOWNLOAD_OK, {
                variant: SnackbarVariants.SUCCESS,
            });
        } catch (error) {
            showAlertError(error, Messages.CONTRACT_DOWNLOAD_ERROR);
        } finally {
            setIsLoading(false);
        }
    }

    const accordion: EfeAccordionValues = {
        icon: <ActionIcon />,
        title: `Configuraci\u00F3n de ${CONSIGNEES_NAME}`,
        reactElement: (
            <Box m={4} mb={8} mt={8}>
                {!isLoading && (
                    <ConsigneeForm
                        expedientId={expedientId}
                        submitHandler={saveUserConsigneesConfig}
                        consigneesConfig={consigneesConfig}
                        consignees={consignees}
                    />
                )}
            </Box>
        ),
        expandIcon: true,
        disabled: false,
        expanded: showModal,
    };

    async function overrideHandler() {
        if (consigneesConfig && consigneesConfig.hasConsignees === false) {
            updateModuleStep(false);
            history.push('/admin/auth-client');
        } else {
            setIsLoading(true);
            try {
                const request: ConsigneeTerms = {
                    expedientId: consigneesConfig?.expedientId || 0,
                    hasAdminAccounts: consigneesConfig?.hasAdminAccounts || false,
                    numberConsignees: consigneesConfig?.numberConsignees || '0',
                    hasConsignees: consigneesConfig?.hasConsignees || false,
                };
                await ClientService.syncAccountConsignee(request);
            } catch (error) {
                console.error(error);
            } finally {
                setIsLoading(false);
            }
            updateModuleStep(false);
            const path = nextStep(flow, menu, true);
            history.push(path);
        }
    }

    React.useEffect(() => {
        getUserConfig();
    }, []);

    return (
        <>
            {isLoading && useSpinner(isLoading)}
            {showAlert && (
                <EfeModalMsg
                    error={genericError}
                    open={showAlert}
                    defaultError={defaultError}
                    handleAction={closeAlert}
                />
            )}
            <MainContent>
                <EfeAccordion onChange={() => setShowModal(!showModal)} values={[accordion]} />
            </MainContent>
            <Divider variant='middle' />
            <MainContent>
                <Grid container direction='row' justifyContent='space-between' spacing={5}>
                    {!isLoading && consignees &&
                        consignees.length > 0 &&
                        consignees.map((item: ConsigneeData) => {
                            return (
                                <Grid item xs={12} key={item.kycId}>
                                    <Box
                                        border={'1px solid #ddd'}
                                        borderRadius={'5px'}
                                        pt={6}
                                        pb={6}
                                        pl={6}
                                        pr={6}
                                        onClick={() => handleChange(item.kycId)}>
                                        <Box style={{display: 'inline-block'}}>
                                            <Typography variant={'body1'}>
                                                <CheckSharp /> {CONSIGNEE_NAME}:{' '}
                                                {item.businessDescription}
                                            </Typography>
                                            <Typography>
                                                {'Responsable: ' + item.fullName}
                                            </Typography>
                                        </Box>
                                        <Box
                                            width={'50%'}
                                            style={{float: 'right', display: 'inline-block'}}>
                                            {item.swAccountData && item.swContractSignature && (
                                                <Link
                                                    className={classesLink.bold}
                                                    color='secondary'
                                                    to={''}>
                                                    <Typography
                                                        component={'span'}
                                                        variant='body2'
                                                        className={classesLink.bold}>
                                                        <Box
                                                            onClick={() =>
                                                                downloadContract(item.kycId)
                                                            }
                                                            display='flex'
                                                            component='div'
                                                            justifyContent='flex-end'>
                                                            <Box m={2}>
                                                                <DownloadIcon />
                                                            </Box>
                                                            <Box m={2}>{'Descargar contrato'}</Box>
                                                        </Box>
                                                    </Typography>
                                                </Link>
                                            )}
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    {consignees && consignees.length > 0 && paginationData && (
                        <Grid item xs={12}>
                            <Divider variant='middle' />
                            <EfePagination
                                page={page}
                                totalPages={paginationData?.totalPages || 1}
                                onChange={(value) => getConsigneeList(value)}
                            />
                        </Grid>
                    )}
                </Grid>
            </MainContent>
            {consignees && consignees.length > 0 && <Divider variant='middle' />}

            <Grid container direction='row' justifyContent='space-between'>
                {consigneesConfig &&
                    (consignees.length !== Number(consigneesConfig.numberConsignees) ||
                        kycId > 0) &&
                    views &&
                    views.length > 0 &&
                    views.map((view: EfeViewsArray) => {
                        if (consigneesConfig?.numberConsignees) {
                            return (
                                <Grid item xs={12} key={view.index}>
                                    {view.view}
                                </Grid>
                            );
                        }
                    })}
            </Grid>
            <EfeSave
                flow={flow}
                menu={menu}
                disabled={
                    consigneesConfig && consigneesConfig.hasAdminAccounts
                        ? moduleStep &&
                          consignees.length > 0
                        : true
                }
                overrideHandler={overrideHandler}
                updateModuleStep={updateModuleStep}
                secondaryButton={
                    <Box m={2}>
                        <EfeButtonSecondary
                            fullWidth
                            variant='outlined'
                            onClick={handleAddConsignee}
                            disabled={
                                consigneesConfig
                                    ? Number(consigneesConfig.numberConsignees) <= 0 ||
                                      consignees.length ===
                                          Number(consigneesConfig.numberConsignees)
                                    : true
                            }
                            endIcon={<Add />}>
                            {`Agregar ${CONSIGNEE_NAME}`}
                        </EfeButtonSecondary>
                    </Box>
                }
            />
        </>
    );
}
